import { Link } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import {
  Grid, Typography, Card, CardContent,
  Link as MuiLink, IconButton,
} from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import { useSelector } from '@client/hooks/redux.hooks';
import { getDateTime } from '@sda/utils/date';
import { getCoverImage, getImageLargeUrl } from '@sda/utils/image';
import { getShowPath } from '@sda/utils/url';
import { getSessionUserFlat, getSessionUserAdminStatus } from '@client/selectors/helpers/session';
import { getApplyTzForShow } from '@client/selectors/entities/showsv2';
import { currentShowIdSelector } from '@client/selectors';
import { Media } from '@client/components/common/Media';
import { DocumentTitle } from '@client/components/common/DocumentTitle';
import ActPlaceholderImage from '@client/images/placeholders/artist.jpg';
import { FuzzedMap } from '@client/components/common/Map/FuzzedMap';
import { InlineBackdrop } from '@client/components/common/Backdrop';
import { useShareShow } from '@client/components/routes/Shows/Details/Show/TicketCTA/useShareShow.hook';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { Description } from '@client/components/common/Description';
import { SiteWrapper } from '../SiteWrapper';
import { Section } from '../_ui/library/CardComponents/Section';
import { TicketLoader } from './Details/TicketLoader';
import { PrivateShowBar } from './lib/PrivateShowBar';
import { CannotPurchaseTicket } from './Details/CannotPurchaseTicket';

const useStyles = makeStyles()((theme) => ({
  alertLink: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.text.primary,
    borderRadius: '10px',
    marginBottom: theme.spacing(2),
  },
  alertMessage: {
    flexDirection: 'unset',
    width: '100%',
  },
  siginIn: {
    textAlign: 'center',
    paddingTop: '30px',
  },
  lessMarginBottom: {
    marginBottom: '30px',
  },
  noMarginBottom: {
    marginBottom: '0',
  },
  ticketFormPadding: {
    padding: '9px',
  },
  container: {
    [theme.breakpoints.down('md')]: {
      padding: 0,
      marginBottom: '0px !important',
    },
  },
  location: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  dateLabel: {
    margin: theme.spacing(2, 0),
  },
  offlineDate: {
    display: 'inline-block',
    marginRight: theme.spacing(3),
  },
  onlineHeader: {
    position: 'relative',

    [theme.breakpoints.down('md')]: {
      backgroundColor: theme.palette.grey[300],
      marginBottom: theme.spacing(2),
      marginLeft: theme.spacing(3 * -1),
      marginRight: theme.spacing(3 * -1),
      marginTop: theme.spacing(3 * -1),
      padding: theme.spacing(3),
    },
  },
  ticketLoaderGridItem: {
    position: 'sticky',
    top: 0, // 112 = MuiAppBar + MuiToolbar - initial position for logged users
  },
  ticketLoaderGridItemShort: {
    top: 0, // 64 = MuiAppBar - initial position for anonymous users
  },
  relative: {
    position: 'relative',
  },
  sidedoorDetailContainer: {
    marginTop: theme.spacing(2),
  },
  dateLabelDark: {
    margin: theme.spacing(2, 0),
    color: theme.palette.grey[600],
  },
  shareButton: {
    position: 'absolute',
    right: 0,

    [theme.breakpoints.up('md')]: {
      top: theme.spacing(1),
    },
  },
}));

export const Details = ({ showId, show }) => {
  const { classes, cx } = useStyles();

  const isMobile = useIsMobile();
  const applyTz = useSelector(getApplyTzForShow);
  const userIsAdmin = useSelector(getSessionUserAdminStatus);
  const user = useSelector(getSessionUserFlat);
  const currentShowId = useSelector(currentShowIdSelector);

  const { id: userId } = user || {};

  const {
    bioHTML, doorsTime, hasPublicTickets, isOnlineOnly,
    publicImageUrl, startTime, title, fuzzyLatLng,
    city, region,
  } = show;

  const handleShareButtonClick = useShareShow(showId);

  if (!currentShowId) { // Needed for when admins click view show chat link as useMountEffect cleanup runs
    return null;
  }

  return (
    <>
      <DocumentTitle
        title={title}
        description={bioHTML}
        image={getCoverImage(publicImageUrl, ActPlaceholderImage)}
        noIndex={!hasPublicTickets ? 'noindex' : 'all'}
      />

      <SiteWrapper className={classes.container}>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="flex-start"
          spacing={isMobile ? 0 : 5}
        >
          <Grid
            item
            lg={8}
            md={9}
            xs={12}
          >
            <Section
              className={classes.headerSection}
              cardHeader={(
                <>
                  {!isMobile && (
                    <Media
                      fallback={ActPlaceholderImage}
                      images={publicImageUrl}
                      title={title}
                      sizeCallback={getImageLargeUrl}
                      enableCarousel
                    />
                  )}

                  {!hasPublicTickets && <PrivateShowBar />}
                </>
              )}
            >
              <div className={classes.relative}>
                {hasPublicTickets && (
                  <IconButton
                    onClick={handleShareButtonClick}
                    size="large"
                    edge="end"
                    className={classes.shareButton}
                  >
                    <ShareIcon />
                  </IconButton>
                )}
              </div>

              {isMobile && <CannotPurchaseTicket />}

              <Typography
                variant="caption"
                className={classes.dateLabel}
              >
                {isOnlineOnly ? getDateTime(startTime).toFormat('LLLL d @ t ZZZZ') : (
                  <>
                    <span className={classes.offlineDate}>
                      {applyTz(startTime).toFormat('LLLL d')}
                    </span>

                    <span>
                      DOORS:
                      {' '}

                      {applyTz(doorsTime).toFormat('t')}

                      {' | SHOW: '}

                      {applyTz(startTime).toFormat('t')}
                    </span>
                  </>
                )}
              </Typography>

              <Typography
                variant="h5"
                gutterBottom
              >
                {title}
              </Typography>

              {!isMobile && (
                <Description>
                  {bioHTML}
                </Description>
              )}
            </Section>

            {!isOnlineOnly && fuzzyLatLng && (
              <Section title="Location">
                <FuzzedMap location={fuzzyLatLng} />

                <br />

                <Typography
                  variant="body2"
                  className={classes.location}
                >
                  <PlaceIcon />

                  <span>{`${city}, ${region}`}</span>
                </Typography>
              </Section>
            )}
          </Grid>

          {!isMobile && (
            <Grid
              className={cx(classes.ticketLoaderGridItem, !userId && classes.ticketLoaderGridItemShort)}
              lg={4}
              md={3}
              item
            >
              <InlineBackdrop color="success">
                <TicketLoader
                  showId={showId}
                />
              </InlineBackdrop>

              {userIsAdmin && (
                <Card>
                  <CardContent>
                    <MuiLink
                      component={Link}
                      display="block"
                      id="sda-show-details-edit-show-details-link"
                      to={`${getShowPath(show.id)}/edit`}
                      variant="body2"
                      paragraph
                    >
                      <EditIcon />

                      <span>
                        Edit Show Details
                      </span>
                    </MuiLink>

                    <MuiLink
                      display="block"
                      id="sda-show-share-show-link"
                      onClick={handleShareButtonClick}
                      variant="body2"
                    >
                      <ShareIcon />

                      <span>
                        Share Show
                      </span>
                    </MuiLink>
                  </CardContent>
                </Card>
              )}
            </Grid>
          )}
        </Grid>
      </SiteWrapper>
    </>
  );
};

export default Details;
